import React from 'react';
import { useCatalogImport } from '@/pages/catalogImport/store';
import { hasWork, importWorkOf, type ImportWork } from './importWork';

export type UseImportWorkResult<TPending> = {
    /** The work on the file in the store (`importWorkOf`). */
    work: ImportWork;
    /**
     * Whether an action that would touch the work asks first: only a stored
     * file can carry work, and only with some (`hasWork`) is there a dialog.
     */
    asksFirst: boolean;
    /** The action waiting on the owner's answer; `null` while none asks. */
    pending: TPending | null;
    /** Asks with an action (`pending`), or closes the question (`null`). */
    setPending: (pending: TPending | null) => void;
};

/**
 * The work on the stored file, memoized on what it is read from, whether an
 * action over it asks first, and the action waiting on the answer: the trio
 * Upload's Replace the file and Branch's Change the branch share. What an
 * answer does stays with the step.
 */
export const useImportWork = <TPending>(): UseImportWorkResult<TPending> => {
    const { state } = useCatalogImport();
    const {
        editedRows,
        exclusions,
        preview,
        lastNames,
        decisions,
        suggestions,
    } = state;
    const work = React.useMemo(
        () =>
            importWorkOf({
                editedRows,
                exclusions,
                preview,
                lastNames,
                decisions,
                suggestions,
            }),
        [decisions, editedRows, exclusions, lastNames, preview, suggestions],
    );
    const [pending, setPending] = React.useState<TPending | null>(null);

    return {
        work,
        asksFirst: state.file !== null && hasWork(work),
        pending,
        setPending,
    };
};
